import React, { Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getVisibleGroup, findSoloPage } from '../../config/pageGroups';
import api from '../../services/api';
import lazyRetry from '../../lazyRetry';
import './PageGroupTabs.css';

// V71.45 — Sistem menüsü rozetleri (sağlık uyarısı, incelenmemiş hata): yalnız yöneticide, 5 dk önbellek
let rozetOnbellek = null;
const useSistemRozet = (aktif) => {
  const [rozet, setRozet] = useState(() => rozetOnbellek?.data || null);
  useEffect(() => {
    if (!aktif) return undefined;
    if (rozetOnbellek && Date.now() - rozetOnbellek.at < 5 * 60e3) { setRozet(rozetOnbellek.data); return undefined; }
    let iptal = false;
    api.get('/admin/saglik').then(r => {
      const d = r.data?.data || {};
      const hk = (d.kontroller || []).find(k => k.anahtar === 'hata_kayitlari');
      const data = {
        '/admin/saglik': { n: (d.sayilar?.uyari || 0) + (d.sayilar?.hata || 0), ton: d.sayilar?.hata ? 'hata' : 'uyari' },
        '/admin/error-log': { n: parseInt(hk?.ozet, 10) || 0, ton: 'hata' }, // ozet: "N incelenmemiş hata"
      };
      rozetOnbellek = { at: Date.now(), data };
      if (!iptal) setRozet(data);
    }).catch(() => { /* rozetsiz devam */ });
    return () => { iptal = true; };
  }, [aktif]);
  return rozet;
};

const aktifYol = (pathname, i) => pathname === i.to || pathname.startsWith(i.to + '/');

// V71.45 — bölümlü grup (Sistem): bölümsüz sekmeler + bölüm başına açılır menü; aktif bölümde sayfa adı görünür
function BolumMenuleri({ group, visible, pathname, rozet }) {
  const [acik, setAcik] = useState(null);
  const kutuRef = useRef(null);
  useEffect(() => { setAcik(null); }, [pathname]);
  useEffect(() => {
    if (!acik) return undefined;
    const disari = (e) => { if (kutuRef.current && !kutuRef.current.contains(e.target)) setAcik(null); };
    const esc = (e) => { if (e.key === 'Escape') setAcik(null); };
    document.addEventListener('mousedown', disari);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', disari); document.removeEventListener('keydown', esc); };
  }, [acik]);

  const cur = visible.find(i => aktifYol(pathname, i));
  const bolumler = group.sections.map(s => ({ ...s, items: visible.filter(i => i.sec === s.key) })).filter(s => s.items.length);
  return (
    <div className="pg-menus" ref={kutuRef}>
      {visible.filter(i => !i.sec).map(i => (
        <NavLink key={i.to} to={i.to} end className={({ isActive }) => `pg-tab${isActive ? ' active' : ''}`}>{i.label}</NavLink>
      ))}
      {bolumler.map(s => {
        const icinde = cur?.sec === s.key;
        const uyari = s.items.some(i => rozet?.[i.to]?.n > 0);
        return (
          <div key={s.key} className="pg-menu">
            <button
              type="button"
              className={`pg-tab pg-menu-btn${icinde ? ' active' : ''}${acik === s.key ? ' open' : ''}`}
              aria-haspopup="true"
              aria-expanded={acik === s.key}
              onClick={() => setAcik(acik === s.key ? null : s.key)}
              onMouseEnter={() => { if (acik && acik !== s.key) setAcik(s.key); }}
            >
              {s.label}
              {icinde && <span className="pg-menu-cur">{cur.label}</span>}
              {uyari && !icinde && <span className="pg-menu-dot" aria-label="uyarı var" />}
              <svg className="pg-menu-chev" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            {acik === s.key && (
              <div className="pg-menu-pop" role="menu">
                <div className="pg-menu-head">{s.full || s.label}</div>
                {s.items.map(i => (
                  <NavLink key={i.to} to={i.to} role="menuitem" className={`pg-menu-item${aktifYol(pathname, i) ? ' active' : ''}`} onClick={() => setAcik(null)}>
                    <span className="pg-menu-txt"><b>{i.label}</b>{i.desc && <small>{i.desc}</small>}</span>
                    {rozet?.[i.to]?.n > 0 && <span className={`pg-menu-badge ${rozet[i.to].ton}`}>{rozet[i.to].n}</span>}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// V70.172 — GRUP DÜZEYİ ARAÇ (kullanıcı isteği: "bilet düzenleyici sadece bilet
// işlemleri sayfasında değil, uçak operasyonda her sayfada görünsün"): sayfaya
// değil GRUBA bağlı olduğu için şeridin kendisinden açılır. Modal ağır olduğu
// için (A4 önizleme) yalnız açılınca yüklenir; şerit neredeyse her sayfada
// render edildiğinden statik import ana pakete girerdi.
const TicketEditorModal = lazyRetry(() => import('../tools/TicketEditorModal'));

// V71.14.3 — masaüstünde şerit üst çubukla TEK satır olur (TopBar.css html[data-tb-merged]); Dashboard ile aynı eşik
const MERGE_MQ = '(min-width: 1101px) and (min-height: 641px)';
const useWide = () => {
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(MERGE_MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(MERGE_MQ);
    if (!mq) return;
    const on = () => setWide(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, []);
  return wide;
};

// V70.65 — Grup sayfalarının üstünde yatay sekme şeridi: sidebar'daki eski alt menülerin
// yerini alır. Sekmeler gerçek rotalara NavLink'tir; sayfa/rota davranışı değişmez.
export default function PageGroupTabs() {
  const { hasPermission, isAdmin } = useAuth();
  const { pathname } = useLocation();
  const [tool, setTool] = useState(null);
  const scrollRef = useRef(null);
  const wide = useWide();

  // V71 — uzun şeritte (ör. Sistem) aktif sekme görünür alanın dışında kalmasın
  // (sayfa aksiyonları slota sonradan yerleşip şeridi daraltabilir → boyut değişince yeniden hizala)
  useEffect(() => {
    const box = scrollRef.current;
    if (!box) return;
    const fit = () => {
      const el = box.querySelector('.pg-tab.active');
      if (!el) return;
      const c = box.getBoundingClientRect(), r = el.getBoundingClientRect();
      if (r.left < c.left || r.right > c.right) box.scrollLeft += (r.left - c.left) - (c.width - r.width) / 2;
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    return () => ro.disconnect();
  }, [pathname]);

  // V71 — modal body'ye portal: .pg-tabs (sticky + z-index) stacking context'inde hapsolup
  // sidebar/topbar'ın altında kalıyordu. Şerit gizlense de açık modal kapanmaz.
  const modal = tool === 'ticket-editor' ? createPortal(
    <Suspense fallback={null}>
      <TicketEditorModal onClose={() => setTool(null)} />
    </Suspense>,
    document.body
  ) : null;

  // V71 — görünür sekme kararı pageGroups.getVisibleGroup'tan (AppLayout .pg-grouped ile aynı)
  const vg = getVisibleGroup(pathname, hasPermission, isAdmin);
  const rozet = useSistemRozet(isAdmin && vg?.group.key === 'sistem');
  // V71.14.3 — sekmesiz sayfa (grupsuz ya da grupta tek görünür sekme): masaüstünde yalnız sayfa adlı tek satır
  let solo = null;
  if (wide && !vg?.showTabs) {
    const item = vg?.visible.find(i => pathname === i.to || pathname.startsWith(i.to + '/'));
    solo = item ? { label: item.label, emoji: vg.group.emoji, color: vg.group.color } : findSoloPage(pathname);
  }
  const merged = wide && (!!vg?.showTabs || !!solo);
  useEffect(() => {
    if (!merged) return;
    document.documentElement.setAttribute('data-tb-merged', '');
    return () => document.documentElement.removeAttribute('data-tb-merged');
  }, [merged]);

  if (solo) {
    const [sf, st] = solo.color || ['#4338ca', '#6366f1'];
    return (<>
      <div className="pg-tabs pg-solo" style={{ '--pg-from': sf, '--pg-to': st }}>
        <span className="pg-tabs-label"><span className="pg-tabs-emoji">{solo.emoji}</span>{solo.label}</span>
        <div className="pg-tabs-right">
          <div className="pg-tabs-actions" id="pg-tabs-actions" />
        </div>
      </div>
      {modal}
    </>);
  }
  // tek sayfa görünüyorsa şerit gereksiz; modal iki dalda da aynı sırada kalır (yeniden mount olmasın)
  if (!vg || !vg.showTabs) return <>{null}{modal}</>;
  const { group, visible } = vg;

  // V70.66 — grup renk kimliği CSS değişkenleriyle akar (etiket rozeti + aktif sekme gradyanı)
  const [from, to] = group.color || ['#4338ca', '#6366f1'];
  return (<>
    <div className={`pg-tabs${group.sections ? ' pg-tabs-menus' : ''}`} style={{ '--pg-from': from, '--pg-to': to }}>
      <span className="pg-tabs-label"><span className="pg-tabs-emoji">{group.emoji}</span>{group.label}</span>
      {group.sections ? <BolumMenuleri group={group} visible={visible} pathname={pathname} rozet={rozet} /> : (
      <div className="pg-tabs-scroll" ref={scrollRef}>
        {visible.map(i => (
          <NavLink
            key={i.to}
            to={i.to}
            className={({ isActive }) => `pg-tab${isActive || pathname.startsWith(i.to + '/') ? ' active' : ''}`}
          >
            {i.label}
          </NavLink>
        ))}
      </div>
      )}
      <div className="pg-tabs-right">
        {/* V70.172 — grubun HER sayfasında görünen araç (sayfa aksiyonu değil) */}
        {/* V71.25 — Operasyon grubunda yalnız uçak sayfalarında (dept: 'ucak') */}
        {visible.find(i => pathname === i.to || pathname.startsWith(i.to + '/'))?.dept === 'ucak' && (isAdmin || hasPermission('tickets', 'view')) && (
          <button
            type="button"
            className="pg-tabs-tool"
            onClick={() => setTool('ticket-editor')}
            title="THY elektronik biletini MTU Corporate Travel belgesine dönüştür"
          >
            🎫<span>Bilet Düzenleyici</span>
          </button>
        )}
        {/* V70.89 — sayfa-özel aksiyon slotu: aktif sayfa createPortal ile buraya buton basar
            (örn. Bilet İşlemleri → Yenile). Boşken görünmez. */}
        <div className="pg-tabs-actions" id="pg-tabs-actions" />
      </div>
    </div>
    {modal}
  </>);
}
